import { ReactNode, useMemo, useState } from 'react';

import pluralize from 'pluralize';

import {
  IconButton,
  Icons,
  LayoutCard,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@cast/design-system';
import { useShouldLimitConsoleForReporting } from '@cast/shared-trial-ui';
import { useSearchContext } from '@cast/shared-search-ui';

import type { ClusterViewPreset } from '../../_constants/viewTabs';
import { CLUSTER_VIEW_TABS } from '../../_constants/viewTabs';
import {
  ClustersTableSettingsProvider,
  useClustersTableSettings,
} from '../../_context/ClustersTableSettingsContext';
import { useClustersListRowData } from '@cast/features-cluster-shared';
import type { RenderClusterRowActions } from '../../types';
import { ClustersTable } from '../ClustersTable/ClustersTable';

type ClustersListCardProps = {
  emptyStateActions?: ReactNode;
  clusterPathTemplate?: string;
  renderRowActions?: RenderClusterRowActions;
};

const ClustersListCardContent = ({
  emptyStateActions,
  clusterPathTemplate,
  renderRowActions,
}: ClustersListCardProps) => {
  const limitReportingConsole = useShouldLimitConsoleForReporting();
  const [viewPreset, setViewPreset] = useState<ClusterViewPreset>(
    limitReportingConsole ? 'compute' : 'default'
  );
  const { getRowData } = useClustersListRowData();
  const { openSettings } = useClustersTableSettings();
  const searchCtx = useSearchContext();
  const clusterCount = searchCtx?.filteredData?.length ?? 0;
  const showClusterCount = !searchCtx?.isLoading && clusterCount > 0;

  const viewTabs = useMemo(() => {
    if (!limitReportingConsole) {
      return CLUSTER_VIEW_TABS;
    }
    return CLUSTER_VIEW_TABS.filter(
      (tab) => tab.value !== 'default' && tab.value !== 'autoscaling'
    ).map((tab) =>
      tab.value === 'compute' ? { ...tab, label: 'Compute' } : tab
    );
  }, [limitReportingConsole]);

  const effectiveViewPreset = useMemo(() => {
    if (!limitReportingConsole) {
      return viewPreset;
    }
    if (viewPreset === 'default' || viewPreset === 'autoscaling') {
      return 'compute';
    }
    return viewPreset;
  }, [limitReportingConsole, viewPreset]);

  return (
    <LayoutCard
      title={
        showClusterCount
          ? `${clusterCount} ${pluralize('cluster', clusterCount)}`
          : undefined
      }
      headerEndContent={
        <Stack direction="row" alignItems="center" gap={8}>
          <ToggleButtonGroup
            size="sm"
            value={effectiveViewPreset}
            onChange={(_, value) => {
              if (value) {
                setViewPreset(value as ClusterViewPreset);
              }
            }}
          >
            {viewTabs.map((tab) => (
              <ToggleButton
                key={tab.value}
                value={tab.value}
                text={tab.label}
              />
            ))}
          </ToggleButtonGroup>
          {!limitReportingConsole ? (
            <IconButton
              variant="ghost"
              size="sm"
              testId="clusters-view-settings-trigger"
              onClick={openSettings}
            >
              <Icons.Gear />
            </IconButton>
          ) : null}
        </Stack>
      }
      headerDivider
      padding={false}
      testId="clusters-list-card"
    >
      <ClustersTable
        viewPreset={effectiveViewPreset}
        getRowData={getRowData}
        emptyStateActions={emptyStateActions}
        clusterPathTemplate={clusterPathTemplate}
        renderRowActions={renderRowActions}
      />
    </LayoutCard>
  );
};

export const ClustersListCard = ({
  emptyStateActions,
  clusterPathTemplate,
  renderRowActions,
}: ClustersListCardProps) => (
  <ClustersTableSettingsProvider>
    <ClustersListCardContent
      emptyStateActions={emptyStateActions}
      clusterPathTemplate={clusterPathTemplate}
      renderRowActions={renderRowActions}
    />
  </ClustersTableSettingsProvider>
);
